import type { Content } from 'pdfmake/interfaces.js';
import type { FetchLike } from '../screens/documents/viewer-core.js';
import { pdfTable, type RecordPdfT } from '../screens/reports/record-pdf.js';
import { TOKENS } from '../tokens.js';
import { loadPreview, PreviewError } from './preview-core.js';

/**
 * Rapor ekleri (ortak 2026-10-26 madde 2; ADR 0130 §A). Tatbikat raporu yalnız adları ve
 * SHA-256'yı listeliyordu; ortak: "ekleri göstermiyor, bağlantılarını gösteriyor". Tatbikat raporu
 * ve Pre-Docking raporları (ADR 0129 Ek Faz 5) aynı yardımcıyı kullanır.
 *
 * - **Resim (JPG, PNG):** indirme ucundan okunur (gemide `felix-file://attachment/<id>`, ofiste
 *   `/api/office/attachments/<id>`; yetki indirmeyle aynı, ADR 0126 §3), sunucunun türü ve ilk
 *   baytları doğrulanır, PDF'e gömülür. pdfmake yalnız JPEG ve PNG gömer; WebP ve GIF dosya
 *   satırında kalır.
 * - **PDF ve diğer dosyalar** okunmaz: web ofiste mutlak `https` bağlantı olur (oturum ve yetki
 *   ucunda). Gemide PDF Windows'un PDF uygulamasında açılır; `felix-file://` orada çalışmaz,
 *   bağlantı yazılmaz; dosya kaydın adımından açılır ("göz", ADR 0126).
 * - Okunamayan resim (ofise henüz gelmemiş, yetkisiz) kodlu kalır; rapor yine üretilir.
 */

/** Rapora girecek ek: kimlik (indirme ucu; ADR 0126), ad, boyut, tür, SHA-256. */
export interface ReportAttachment {
  id: string;
  fileName: string;
  fileSize: number;
  mimeType: string;
  sha256: string;
  /** Gerekçeli iptal edilmiş ek: listede kalır, rapora gömülmez [NR496 4.3.2a]. */
  voided?: boolean;
}

export interface ReportFile {
  /** Gömülecek resim: `data:image/png;base64,…`. */
  image?: string;
  /** PDF'te açılabilir bağlantı (yalnız http/https). */
  link?: string;
  /** Resim okunamadı ya da gömülmedi: hata kodu. */
  unavailable?: string;
}

/** Ek kimliğine göre rapordaki karşılığı; listede olmayan ek yalnız adıyla yazılır. */
export type ReportFiles = Readonly<Record<string, ReportFile>>;

const EMBED_TYPES = new Set(['image/jpeg', 'image/png']);
/** Gömülen tek resmin üst sınırı; gemide fotoğraf yüklemede küçültülür (K15). */
export const MAX_EMBED_BYTES = 10 * 1024 * 1024;
/** Bir raporun gömülü resimlerinin toplamı; aşan resim dosya satırında kalır. */
export const MAX_EMBED_TOTAL_BYTES = 40 * 1024 * 1024;

/** Baytların data URL'si (tarayıcıda ve Node'da `btoa`). */
export function bytesToDataUrl(type: string, bytes: Uint8Array): string {
  let binary = '';
  const chunk = 0x8000;
  for (let i = 0; i < bytes.length; i += chunk) {
    binary += String.fromCharCode(...bytes.subarray(i, i + chunk));
  }
  return `data:${type};base64,${btoa(binary)}`;
}

/** Ek adresinin PDF'e yazılabilecek mutlak biçimi: yalnız http ve https; yoksa `null`. */
export function reportLinkOf(url: string, base: string | null): string | null {
  if (!base) return null;
  try {
    const absolute = new URL(url, base);
    return absolute.protocol === 'https:' || absolute.protocol === 'http:' ? absolute.href : null;
  } catch {
    return null;
  }
}

function codeOf(error: unknown): string {
  if (error instanceof PreviewError) return error.code;
  const code = (error as { code?: unknown } | null)?.code;
  return typeof code === 'string' ? code : 'READ_FAILED';
}

/**
 * Raporun eklerini hazırlar. `url` ekin okunabilir adresi (kaynak ya da uygulamanın ek
 * erişimi), `base` sayfanın adresi (web ofiste mutlak bağlantı için; gemide bağlantı çıkmaz).
 */
export async function loadReportFiles(
  attachments: readonly ReportAttachment[],
  options: { url: (id: string) => string; base: string | null; fetch?: FetchLike },
): Promise<Record<string, ReportFile>> {
  const out: Record<string, ReportFile> = {};
  let total = 0;
  for (const attachment of attachments) {
    if (attachment.voided || !attachment.id) continue;
    const url = options.url(attachment.id);
    const link = reportLinkOf(url, options.base);
    const entry: ReportFile = link ? { link } : {};
    if (EMBED_TYPES.has(attachment.mimeType.toLowerCase())) {
      if (
        attachment.fileSize > MAX_EMBED_BYTES ||
        total + attachment.fileSize > MAX_EMBED_TOTAL_BYTES
      ) {
        entry.unavailable = 'TOO_LARGE_TO_EMBED';
      } else {
        try {
          const { blob } = await loadPreview(url, options.fetch);
          // Sunucunun türü esastır (K15): yalnız JPEG ve PNG gömülür
          if (!EMBED_TYPES.has(blob.type) || blob.type !== attachment.mimeType.toLowerCase()) {
            throw new PreviewError('PREVIEW_UNSUPPORTED');
          }
          if (blob.size > MAX_EMBED_BYTES) throw new PreviewError('TOO_LARGE_TO_EMBED');
          total += blob.size;
          entry.image = bytesToDataUrl(blob.type, new Uint8Array(await blob.arrayBuffer()));
        } catch (error) {
          entry.unavailable = codeOf(error);
        }
      }
    }
    if (Object.keys(entry).length > 0) out[attachment.id] = entry;
  }
  return out;
}

const KB = (bytes: number) => `${Math.max(1, Math.round(bytes / 1024))} KB`;

/** Bölümün metinleri: `<prefix>.values.*`, `<prefix>.columns.*`, `<prefix>.none.attachments`. */
export interface ReportAttachmentKeys<A extends ReportAttachment> {
  prefix: string;
  /** Ekin raporda bağlı olduğu yer (tatbikatta adım no, denetimde kontrol noktası). */
  ref: (attachment: A) => string | number;
}

/**
 * "Attachments" (ortak 2026-10-26 madde 2; ADR 0130 §A): resimler raporun içinde, yeri ve dosya
 * adıyla altyazılı, ikişer yan yana; PDF ve diğer dosyalar tabloda, web ofiste açılabilir
 * bağlantıyla (gemide "Felixmate'da açın"). SHA-256 denetim için küçük yazıyla kalır.
 * İptal edilen ek gömülmez, işaretli kalır [NR496 4.3.2a].
 */
export function reportAttachmentSection<A extends ReportAttachment>(
  t: RecordPdfT,
  attachments: readonly A[],
  files: ReportFiles,
  keys: ReportAttachmentKeys<A>,
): Content[] {
  const v = (key: string, options?: Record<string, unknown>) =>
    t(`${keys.prefix}.values.${key}`, options);
  const c = (key: string) => t(`${keys.prefix}.columns.${key}`);
  if (attachments.length === 0) {
    return [{ text: t(`${keys.prefix}.none.attachments`), style: 'muted' }];
  }
  const hash = (sha: string): Content => ({
    text: `SHA-256 ${sha || '—'}`,
    fontSize: 5.5,
    color: TOKENS['text-secondary'],
    margin: [0, 1, 0, 0],
  });
  const caption = (a: A) => v('attachmentCaption', { no: keys.ref(a), name: a.fileName });
  const images = attachments.filter((a) => !a.voided && files[a.id]?.image);
  const others = attachments.filter((a) => !images.includes(a));
  const out: Content[] = [];
  for (let i = 0; i < images.length; i += 2) {
    out.push({
      columns: images.slice(i, i + 2).map((a) => ({
        width: '50%',
        unbreakable: true,
        stack: [
          { image: files[a.id]?.image ?? '', fit: [250, 190], margin: [0, 2, 0, 2] },
          { text: caption(a), style: 'label', fontSize: 8 },
          hash(a.sha256),
        ],
      })),
      columnGap: 12,
      margin: [0, 2, 0, 6],
    });
  }
  if (others.length > 0) {
    out.push(
      pdfTable(
        [c('step'), c('file'), c('size'), c('open')],
        others.map((a) => {
          const file = files[a.id];
          const link = !a.voided ? file?.link : undefined;
          const notes = [
            a.voided ? v('voided') : null,
            !a.voided && file?.unavailable ? v('notEmbedded') : null,
          ].filter(Boolean);
          return [
            String(keys.ref(a)),
            {
              stack: [
                link
                  ? {
                      text: a.fileName,
                      link,
                      color: TOKENS['primary-600'],
                      decoration: 'underline',
                    }
                  : { text: a.fileName },
                ...(notes.length > 0 ? [{ text: notes.join(' · '), style: 'muted' }] : []),
                hash(a.sha256),
              ],
            },
            KB(a.fileSize),
            a.voided
              ? '—'
              : link
                ? { text: v('openLink'), link, color: TOKENS['primary-600'] }
                : v('openInApp', { no: keys.ref(a) }),
          ];
        }),
        [30, '*', 50, 130],
      ),
    );
  }
  return out;
}
